import { useEffect, useState, type Dispatch } from "react";
import { api, SYNC_PENDING_TEXT } from "../api/client";
import { explorerTx } from "../env";
import { usePlayer } from "../state/PlayerContext";
import { t } from "../strings";
import { formatRcd, formatSol, timeAgo } from "./format";
import type { Banner, OverlayAction, OverlayState } from "./state";

const nameCache = new Map<string, string>();

function useMonName(asset: string | undefined, fallback: string | undefined): string {
  const [name, setName] = useState(fallback ?? (asset ? nameCache.get(asset) : undefined) ?? "A Pokémon");
  useEffect(() => {
    if (fallback || !asset) return;
    const cached = nameCache.get(asset);
    if (cached) return setName(cached);
    let live = true;
    api
      .monMetadata(asset)
      .then((m) => {
        nameCache.set(asset, m.name);
        if (live) setName(m.name);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [asset, fallback]);
  return name;
}

function BannerView({ banner, onDismiss }: { banner: Banner; onDismiss: () => void }) {
  const name = useMonName(banner.asset, banner.name);
  let text: string;
  let tone = "info";
  switch (banner.kind) {
    case "ownership_changed":
      text = banner.direction === "in" ? t.ownershipArrived(name) : t.ownershipChanged(name);
      break;
    case "released":
      text = t.released(name, banner.lamports ?? "0");
      tone = "ok";
      break;
    case "vault_funded":
      text = t.vaultFunded;
      tone = "ok";
      break;
    case "taint":
      text = banner.text ?? t.glitch;
      tone = "warn";
      break;
    case "offline_discarded":
      text = banner.text ?? "";
      tone = "warn";
      break;
    default:
      text = banner.text ?? "";
  }
  return (
    <div className={`banner ${tone}`} role="status">
      <span>{text}</span>
      <button className="dismiss" aria-label="Dismiss" onClick={onDismiss}>
        ×
      </button>
    </div>
  );
}

function StatusLine({ s }: { s: OverlayState }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  if (s.superseded) return <div className="status-line error">{t.superseded}</div>;
  if (s.autosave && s.autosave.until > Date.now()) return <div className="status-line autosaved">{s.autosave.text}</div>;
  if (s.syncPendingWaiting) return <div className="status-line">{SYNC_PENDING_TEXT}</div>;
  switch (s.phase) {
    case "uploading":
      return <div className="status-line">{s.online ? t.uploading : t.offline}</div>;
    case "verifying":
      return <div className="status-line">{t.verifying}</div>;
    case "syncing":
      return (
        <div className="status-line">
          {t.syncing(s.progress?.done ?? 0, s.progress?.total ?? 0)}
          <progress max={s.progress?.total || 1} value={s.progress?.done ?? 0} />
        </div>
      );
    case "synced":
      return (
        <div className="status-line ok">
          {t.synced}
          {s.signature && (
            <>
              {" · "}
              <a href={explorerTx(s.signature)} target="_blank" rel="noreferrer">
                View on explorer
              </a>
            </>
          )}
        </div>
      );
    case "rejected":
      return (
        <div className="status-line error">
          {t.rejected(s.lastSyncedAt ? new Date(s.lastSyncedAt).toLocaleString() : "before this session")}{" "}
          <a href="/help#support" target="_blank" rel="noreferrer">
            Contact support
          </a>
        </div>
      );
    default:
      return <div className="status-line">{s.offlineSession && !s.online ? t.offline : t.idle(timeAgo(s.lastSyncedAt))}</div>;
  }
}

/**
 * Thin sync/status strip under the stage. Claim / Deposit / Vault / Fees live in the
 * Start-menu WALLET Gen1 UI (see wallet-menu/), not beside the canvas.
 */
export function Overlay({ state, dispatch }: { state: OverlayState; dispatch: Dispatch<OverlayAction> }) {
  const { me } = usePlayer();

  useEffect(() => {
    if (!state.autosave) return;
    const ms = Math.max(0, state.autosave.until - Date.now());
    const id = setTimeout(() => dispatch({ type: "autosaveExpired", now: Date.now() }), ms + 10);
    return () => clearTimeout(id);
  }, [state.autosave, dispatch]);

  useEffect(() => {
    if (state.phase !== "synced") return;
    const id = setTimeout(() => dispatch({ type: "settle" }), 10_000);
    return () => clearTimeout(id);
  }, [state.phase, state.signature, dispatch]);

  const waiting = state.waiting;
  const coveredByWallet = !!waiting && !!me && BigInt(me.walletAllowance.usable) >= BigInt(waiting.mintRcdNeeded);
  const mintNote =
    waiting && waiting.pendingMints > 0
      ? waiting.rentPoolShort
        ? t.mintsPoolPaused(waiting.pendingMints)
        : BigInt(waiting.mintRcdNeeded) > 0n
          ? coveredByWallet
            ? t.mintsFromWallet(waiting.pendingMints)
            : t.mintsNeedRcd(waiting.pendingMints, waiting.mintRcdNeeded)
          : null
      : null;
  const itemsTopUp = waiting && waiting.pendingItemBag && BigInt(waiting.topUpNeeded) > 0n ? waiting.topUpNeeded : null;
  const feeDebt = me && BigInt(me.feeDebtRcd) > 0n ? me.feeDebtRcd : null;

  return (
    <aside className="overlay overlay--strip" aria-live="polite">
      <StatusLine s={state} />
      {mintNote && (
        <div className="banner warn">
          <span>{mintNote}</span>
          {!waiting?.rentPoolShort && !coveredByWallet && (
            <span className="hint"> Open Start → WALLET to deposit or set a spending limit.</span>
          )}
        </div>
      )}
      {itemsTopUp && (
        <div className="banner warn">
          <span>{t.needsSolItems(itemsTopUp)}</span>
          <span className="hint"> Open Start → WALLET → VAULT.</span>
        </div>
      )}
      {feeDebt && <div className="banner warn">{t.feeDebt(feeDebt)}</div>}
      {state.banners.map((b) => (
        <BannerView key={b.id} banner={b} onDismiss={() => dispatch({ type: "dismiss", id: b.id })} />
      ))}
      {me && BigInt(me.vault.lamports) > 0n && (
        <p className="hint small">
          Vault {formatSol(me.vault.lamports)} SOL · in-game {formatRcd(me.gameBalance)} · Start → WALLET
        </p>
      )}
    </aside>
  );
}
